import React, { useState, useEffect } from 'react';
import {
  Key,
  Shield,
  ShieldCheck,
  Plus,
  RefreshCw,
  Trash2,
  Copy,
  Check,
  Eye,
  EyeOff,
  AlertTriangle,
  X,
  Lock,
  ArrowRight
} from 'lucide-react';
import {
  verifyPasscode,
  getApiKeys,
  updatePrimaryKey,
  addApiKey,
  setActiveKey,
  deleteApiKey,
  resetApiKey
} from '../utils/api';
import { useToast } from '../hooks/useToast';

export default function AdminModal({ isOpen, onClose }) {
  const toast = useToast();
  const [passcode, setPasscode] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [keys, setKeys] = useState([]);
  const [activeKey, setActiveKeyObj] = useState(null);
  const [loading, setLoading] = useState(false);

  // Direct Change Active Key state
  const [editActiveKey, setEditActiveKey] = useState('');
  const [savingPrimary, setSavingPrimary] = useState(false);

  // Add Fallback Key state
  const [newKey, setNewKey] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [newPriority, setNewPriority] = useState(0);
  const [addingKey, setAddingKey] = useState(false);

  // UI state
  const [showFullKeys, setShowFullKeys] = useState({});
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    if (isOpen && authenticated) {
      loadKeys();
    }
  }, [isOpen, authenticated]);

  const loadKeys = async () => {
    try {
      setLoading(true);
      const res = await getApiKeys();
      if (res.data.success) {
        setKeys(res.data.data);
        if (res.data.activeKey) {
          setActiveKeyObj(res.data.activeKey);
          setEditActiveKey(res.data.activeKey.key);
        }
      }
    } catch (err) {
      toast('Failed to load API keys', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!passcode.trim()) return;
    try {
      setLoading(true);
      const res = await verifyPasscode(passcode.trim());
      if (res.data.success) {
        setAuthenticated(true);
        toast('Admin access granted', 'success');
      }
    } catch (err) {
      toast('Incorrect passcode. Please enter 741501', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Direct update of primary SerpAPI key
  const handleUpdatePrimary = async (e) => {
    e.preventDefault();
    if (!editActiveKey.trim()) {
      toast('Please enter a valid SerpAPI key', 'error');
      return;
    }
    setSavingPrimary(true);
    try {
      const res = await updatePrimaryKey({
        key: editActiveKey.trim(),
        label: 'Primary SerpAPI Key'
      });
      if (res.data.success) {
        toast('Primary SerpAPI key updated successfully!', 'success');
        await loadKeys();
      }
    } catch (err) {
      toast(err.response?.data?.error || 'Failed to update key', 'error');
    } finally {
      setSavingPrimary(false);
    }
  };

  // Add fallback key
  const handleAddFallback = async (e) => {
    e.preventDefault();
    if (!newKey.trim()) return;
    setAddingKey(true);
    try {
      const res = await addApiKey({
        key: newKey.trim(),
        label: newLabel.trim() || `Fallback Key ${keys.length + 1}`,
        priority: Number(newPriority) || 0
      });
      if (res.data.success) {
        toast('Fallback key added to pool', 'success');
        setNewKey('');
        setNewLabel('');
        setNewPriority(0);
        await loadKeys();
      }
    } catch (err) {
      toast(err.response?.data?.error || 'Failed to add key', 'error');
    } finally {
      setAddingKey(false);
    }
  };

  const handleSetActive = async (id) => {
    try {
      const res = await setActiveKey(id);
      if (res.data.success) {
        toast('Key promoted to primary active key', 'success');
        await loadKeys();
      }
    } catch (err) {
      toast('Failed to set active key', 'error');
    }
  };

  const handleReset = async (id) => {
    try {
      const res = await resetApiKey(id);
      if (res.data.success) {
        toast('Key limit counter reset to active', 'success');
        await loadKeys();
      }
    } catch (err) {
      toast('Failed to reset key', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this API key from the fallback pool?')) return;
    try {
      const res = await deleteApiKey(id);
      if (res.data.success) {
        toast('Key deleted', 'success');
        await loadKeys();
      }
    } catch (err) {
      toast('Failed to delete key', 'error');
    }
  };

  const copyKey = (id, keyText) => {
    navigator.clipboard.writeText(keyText);
    setCopiedId(id);
    toast('Key copied to clipboard', 'success');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const toggleShowKey = (id) => {
    setShowFullKeys(prev => ({ ...prev, [id]: !prev[id] }));
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="modal-header">
          <div className="modal-title">
            <Key size={18} color="var(--color-indigo)" />
            <span>API Key Management</span>
            {authenticated && (
              <span className="badge badge-success" style={{ marginLeft: '0.5rem' }}>
                <ShieldCheck size={12} />
                Admin Active
              </span>
            )}
          </div>
          <button className="modal-close-btn" onClick={onClose} title="Close">
            <X size={18} />
          </button>
        </div>

        <div className="modal-body">
          {!authenticated ? (
            /* Passcode Form */
            <form onSubmit={handleVerify} style={{ textAlign: 'center', padding: '1.5rem 0' }}>
              <div style={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                backgroundColor: 'var(--color-indigo-subtle)',
                color: 'var(--color-indigo)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem'
              }}>
                <Lock size={22} />
              </div>
              <h3 style={{ fontSize: '1.15rem', fontWeight: 700, marginBottom: '0.5rem' }}>
                Admin Authentication
              </h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: '1.5rem', maxWidth: 360, marginInline: 'auto' }}>
                Enter the 6-digit admin passcode to manage SerpAPI keys and automatic fallback pools.
              </p>

              <div style={{ maxWidth: 280, margin: '0 auto 1.25rem' }}>
                <input
                  type="password"
                  className="form-input"
                  style={{ textAlign: 'center', fontSize: '1.25rem', letterSpacing: '0.25em', padding: '0.75rem' }}
                  placeholder="••••••"
                  maxLength={10}
                  value={passcode}
                  onChange={e => setPasscode(e.target.value)}
                  autoFocus
                  required
                />
              </div>

              <button
                type="submit"
                className="btn btn-primary"
                style={{ width: 280 }}
                disabled={loading || !passcode.trim()}
              >
                {loading ? <span className="spinner" /> : (
                  <>
                    Unlock Panel
                    <ArrowRight size={15} />
                  </>
                )}
              </button>
            </form>
          ) : (
            /* Authenticated Admin Panel */
            <>
              {/* Current Active Key Card */}
              <div className="admin-card" style={{ borderColor: 'var(--color-indigo-border)' }}>
                <div className="admin-card-header">
                  <div className="admin-card-title">
                    <Shield size={16} color="var(--color-indigo)" />
                    <span>Current Active SerpAPI Key</span>
                  </div>
                  <span className={`badge ${activeKey?.isExhausted ? 'badge-danger' : 'badge-success'}`}>
                    {activeKey?.isExhausted ? 'Limit Exhausted' : 'Active & Ready'}
                  </span>
                </div>

                <form onSubmit={handleUpdatePrimary}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <input
                        type="text"
                        className="form-input"
                        style={{ flex: 1, fontFamily: 'var(--font-mono)', fontSize: '0.825rem' }}
                        value={editActiveKey}
                        onChange={e => setEditActiveKey(e.target.value)}
                        placeholder="Paste your SerpAPI Key (e.g. 017e54d2...)"
                        required
                      />
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={savingPrimary || !editActiveKey.trim()}
                      >
                        {savingPrimary ? <span className="spinner" /> : 'Save & Set Active'}
                      </button>
                    </div>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                      Changes take effect immediately for all searches.
                    </span>
                  </div>
                </form>
              </div>

              {/* Add Fallback Key */}
              <div className="admin-card">
                <div className="admin-card-header">
                  <div className="admin-card-title">
                    <Plus size={16} color="var(--text-secondary)" />
                    <span>Add Fallback / Backup Key</span>
                  </div>
                </div>

                <form onSubmit={handleAddFallback}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1.8fr 1fr 0.8fr auto', gap: '0.65rem', alignItems: 'flex-end' }}>
                    <div className="form-group">
                      <label className="form-label">SerpAPI Key *</label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="Backup API key..."
                        value={newKey}
                        onChange={e => setNewKey(e.target.value)}
                        style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8rem' }}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Label</label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="e.g. Backup Key 2"
                        value={newLabel}
                        onChange={e => setNewLabel(e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Priority</label>
                      <input
                        type="number"
                        className="form-input"
                        placeholder="0"
                        value={newPriority}
                        onChange={e => setNewPriority(e.target.value)}
                      />
                    </div>
                    <button
                      type="submit"
                      className="btn btn-secondary"
                      disabled={addingKey || !newKey.trim()}
                    >
                      {addingKey ? <span className="spinner" /> : <Plus size={15} />}
                      Add Key
                    </button>
                  </div>
                </form>
              </div>

              {/* Keys Pool List */}
              <div className="admin-card">
                <div className="admin-card-header">
                  <div className="admin-card-title">
                    <Key size={16} color="var(--text-secondary)" />
                    <span>Configured Key Pool ({keys.length})</span>
                  </div>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                    Auto-falls back to next key when search limit exhausts
                  </span>
                </div>

                {loading ? (
                  <div style={{ textAlign: 'center', padding: '1.5rem', color: 'var(--text-muted)' }}>
                    <span className="spinner" style={{ margin: '0 auto 0.5rem' }} />
                    Loading keys...
                  </div>
                ) : keys.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '1.5rem', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                    No keys configured. Add an API key above to start.
                  </div>
                ) : (
                  <div className="key-list">
                    {keys.map((k) => {
                      const isShowing = showFullKeys[k.id];
                      const displayedText = isShowing ? k.fullKey : k.maskedKey;

                      return (
                        <div key={k.id} className="key-item">
                          <div className="key-item-info">
                            <div className="key-item-label">
                              <span>{k.label}</span>
                              {k.isPrimary && (
                                <span className="badge badge-indigo">Primary</span>
                              )}
                              <span className={`badge ${k.isExhausted ? 'badge-danger' : 'badge-success'}`}>
                                {k.isExhausted ? 'Limit Exhausted' : 'Ready'}
                              </span>
                              {k.usageCount > 0 && (
                                <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                                  ({k.usageCount} searches)
                                </span>
                              )}
                            </div>
                            <div className="key-item-code">
                              {displayedText}
                            </div>
                          </div>

                          <div className="key-item-actions">
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              onClick={() => toggleShowKey(k.id)}
                              title={isShowing ? 'Hide Key' : 'Reveal Key'}
                            >
                              {isShowing ? <EyeOff size={13} /> : <Eye size={13} />}
                            </button>

                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              onClick={() => copyKey(k.id, k.fullKey)}
                              title="Copy Key"
                            >
                              {copiedId === k.id ? <Check size={13} color="var(--color-success)" /> : <Copy size={13} />}
                            </button>

                            {!k.isPrimary && (
                              <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                onClick={() => handleSetActive(k.id)}
                                title="Promote to Primary Active Key"
                              >
                                Set Active
                              </button>
                            )}

                            {k.isExhausted && (
                              <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                onClick={() => handleReset(k.id)}
                                title="Reset Exhausted Status"
                              >
                                <RefreshCw size={13} />
                                Reset
                              </button>
                            )}

                            {!k.isPrimary && (
                              <button
                                type="button"
                                className="btn btn-danger-outline btn-sm"
                                onClick={() => handleDelete(k.id)}
                                title="Delete Key"
                              >
                                <Trash2 size={13} />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
